import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { SecureStorage } from '../storage/secure-storage';
import { ApiFeedbackService } from '../api-feedback.service';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import {Multimedia} from '@/shared/models/multimedias.model';
import {BaseResponse} from '@/shared/models';

/** Interface for user profile API response */
export interface UserProfile {
  multimedia: Multimedia | null;
  name: string;
  roleName: string;
  rolePrefix: string;
  typeAdvisorPrefix: string;
  realStateMultimedia: Multimedia | null;
  realStateName: string | null;
}

@Injectable({ providedIn: 'root' })
export class UserProfileService {
  private _loading$ = new BehaviorSubject<boolean>(false);
  readonly loading$ = this._loading$.asObservable();

  private _current$ = new BehaviorSubject<UserProfile | null>(null);
  readonly current$ = this._current$.asObservable();

  constructor(
    private http: HttpClient,
    private storage: SecureStorage,
    private feedback: ApiFeedbackService
  ) {}

  /** Returns the current user profile snapshot */
  get snapshot(): UserProfile | null {
    return this._current$.value;
  }

  /** Loads user profile from API */
  loadProfile() {
    this._loading$.next(true);

    return this.http
      .get<BaseResponse<UserProfile>>(
        `${environment.apiBase}/me/my-profile`,
        { headers: this.authHeaders() }
      )
      .pipe(
        map(res => res.data),
        tap(data => this._current$.next(data)),
        finalize(() => this._loading$.next(false)),
        catchError(err => {
          const msg = err.error?.message ?? 'Error loading user profile';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  /** Helper to add Authorization header */
  private authHeaders(): HttpHeaders {
    const token = this.storage.get('access');
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }
}
